import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * AI Agent Node Schema (v2.0 - Sub-Node Architecture)
 * 
 * This is the refactored AI Agent that uses visual sub-node connections instead of
 * built-in configuration. Connect:
 * - Chat Model sub-nodes (Claude, OpenAI, etc.)
 * - Memory sub-nodes (Simple Memory, Vector Store, etc.)
 * - Tool-capable nodes (HTTP Request, Code, etc.)
 */
const schema: NodePropertySchema = {
  nodeType: 'ai-agent',
  version: '2.0.0',
  
  properties: [
    // ==================================================================
    // PROMPTS
    // ==================================================================
    {
      key: 'systemPrompt',
      label: 'System Prompt',
      type: 'text',
      typeOptions: {
        rows: 6
      },
      default: 'You are a helpful AI assistant.',
      placeholder: 'You are a helpful assistant that...',
      description: 'System instructions that guide the AI behavior',
      required: false
    },
    
    {
      key: 'userPrompt',
      label: 'User Message',
      type: 'text',
      typeOptions: {
        rows: 4
      },
      placeholder: '{{ $node["NodeName"].string }}',
      description: 'The user message or question to send to the AI',
      required: true
    },
    
    // ==================================================================
    // AGENT BEHAVIOR
    // ==================================================================
    {
      key: 'responseStyle',
      label: 'Response Style',
      type: 'options',
      default: 'concise',
      options: [
        { name: 'Concise', value: 'concise', description: 'Direct answers only, no explanations' },
        { name: 'Detailed', value: 'detailed', description: 'Include reasoning and methodology' },
        { name: 'Custom', value: 'custom', description: 'Control via system prompt' }
      ],
      description: 'Controls the verbosity of the AI response',
      section: 'settings'
    },

    {
      key: 'requiresAction',
      label: 'Requires Action',
      type: 'boolean',
      default: false,
      description: 'If true, agent must use at least one tool before responding',
      section: 'settings'
    },

    {
      key: 'binaryHandling',
      label: 'File Handling',
      type: 'options',
      default: 'auto',
      options: [
        { name: 'Auto-Detect', value: 'auto', description: 'Automatically detect files from any upstream node (Outlook, FTP, OneDrive, SharePoint, HTTP, etc.)' },
        { name: 'Ignore Files', value: 'ignore', description: 'Do not include any binary files with the message' },
        { name: 'Specific Field', value: 'field', description: 'Navigate to a specific field path in the input data to find the binary file' }
      ],
      description: 'How to handle binary files (images, PDFs, text files) from upstream nodes. Auto-Detect works with all built-in nodes.',
      section: 'settings'
    },

    {
      key: 'binaryFieldName',
      label: 'Binary Field Path',
      type: 'string',
      default: '',
      placeholder: 'e.g., messages[0].attachments[0] or binary.data',
      description: 'Dot-notation path to the object containing binary data (e.g., messages[0].attachments[0]). Points to the object with data/content and mimeType/contentType properties — not the base64 value itself.',
      displayOptions: {
        show: {
          binaryHandling: ['field']
        }
      },
      section: 'settings'
    },

    {
      key: 'maxIterations',
      label: 'Max Iterations',
      type: 'number',
      default: 10,
      typeOptions: {
        minValue: 1,
        maxValue: 50
      },
      description: 'Maximum number of tool calls the agent can make in a single execution',
      section: 'settings'
    },
    
    // ==================================================================
    // OUTPUT OPTIONS
    // ==================================================================
    {
      key: 'returnIntermediateSteps',
      label: 'Return Intermediate Steps',
      type: 'boolean',
      default: false,
      description: 'Include tool calls and intermediate results in the output',
      section: 'settings'
    },
    
    {
      key: 'outputKey',
      label: 'Output Key',
      type: 'string',
      default: 'output',
      placeholder: 'output',
      description: 'The key to use for the agent response in the output JSON',
      section: 'settings'
    }
  ],
  
  metadata: {
    author: 'Evo E2E Team',
    documentation: 'https://docs.evoe2e.com/nodes/ai-agent'
  }
};

export default schema;
